"use client";

import { cn, shadow } from "@/lib/utils";
import { IBlocks, IForm, ITheme, IUserData } from "@/types";
import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import type { BlockUpdateFunction } from "./utils/blocks-map";
import AdminPopover from "./utils/AdminPopover";
import RenderIconOrAsset from "./utils/RenderIconOrAsset";
import FormRender from "@/components/admin/forms/Render";
import BlockClickHandler from "./utils/click";
import {
    BORDER_RADIUS_OPTIONS,
    FONT_SIZE_OPTIONS,
    SHADOW_OPTIONS,
} from "./utils/block-style-classes";
interface Props {
    edit?: boolean;
    account: IUserData;
    block: IBlocks;
    update?: BlockUpdateFunction;
    style: any;
    theme: IUserData["chosen_theme"];
}
export default function BlockForm({
    edit,
    account,
    block,
    update,
    style,
    theme,
}: Props) {
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const [data, setData] = React.useState<IForm | null>(null);
    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(
                    `/api/account/${account.id}/form/${blockUpdate.options?.form_id}`,
                    { cache: "no-store" }
                );
                const data = await response.json();
                setData(data.form);
            } catch (error) {
                console.error(error);
                setData(null);
            }
        };
        fetchData();
    }, [account, blockUpdate.options?.form_id]);
    return (
        <AdminPopover
            edit={edit}
            className={block.show ? "" : "opacity-30"}
            title={"Form"}
            block={block}
            update={update}
            setBlockUpdate={setBlockUpdate}
        >
            <div key={block.id} className={`w-full`} id="Form">
                {data ? (
                    <Sheet>
                        <SheetTrigger asChild>
                            <button
                                style={style}
                                id="block"
                                className={cn(
                                    "rounded-user bg-user-primary-color text-user-primary-text-color",
                                    SHADOW_OPTIONS[theme.profile.design.shadow]
                                        ?.class,
                                    blockUpdate.style?.custom &&
                                        BORDER_RADIUS_OPTIONS[
                                            blockUpdate.style.corner_radius
                                        ]?.class,
                                    blockUpdate.style?.custom &&
                                        SHADOW_OPTIONS[blockUpdate.style.shadow]
                                            ?.class,
                                    blockUpdate.style?.custom &&
                                        FONT_SIZE_OPTIONS[
                                            blockUpdate.style.font_size
                                        ]?.class,
                                    "w-full px-1 py-3 text-xl transition-all duration-150 hover:scale-105",
                                    blockUpdate.name.replaceAll(" ", "-"),
                                    "block-" + block.id
                                )}
                                onClick={(e) => {
                                    BlockClickHandler(account, e, block);
                                }}
                            >
                                <RenderIconOrAsset
                                    account_id={account.id}
                                    iconName={blockUpdate.icon}
                                    size={
                                        (blockUpdate.style?.custom &&
                                            FONT_SIZE_OPTIONS[
                                                blockUpdate.style.font_size
                                            ]?.icon) ||
                                        FONT_SIZE_OPTIONS[
                                            theme.profile.design.font_size
                                        ]?.icon ||
                                        26
                                    }
                                />
                                <span
                                    id="block-text"
                                    className={`block w-full px-6 text-center font-bold`}
                                >
                                    {blockUpdate.name.length > 0
                                        ? blockUpdate.name
                                        : data.name}
                                </span>
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            // className=" h-full w-full overflow-hidden border-form-primary-color bg-form-background-color  sm:h-fit sm:max-h-[80dvh]"
                            className="w-full max-w-full p-0 md:w-1/2 md:max-w-full md:border-l md:border-form-primary-color"
                            onOpenAutoFocus={(e) => e.preventDefault()}
                        >
                            <SheetHeader className="sr-only">
                                <SheetTitle className="sr-only"></SheetTitle>
                            </SheetHeader>
                            <div className="h-full overflow-auto p-3">
                                <FormRender account={account} form={data} />
                            </div>
                        </SheetContent>
                    </Sheet>
                ) : (
                    <Skeleton className="h-10 w-full rounded-md bg-user-primary-color" />
                )}
            </div>
        </AdminPopover>
    );
}
